import type { FormHistory } from '../../../services/formService';
import { formatDollars } from '../../../utils/formatDollars';
import { EMPTY_MARK } from '@/components/ui';

// V2-061 – form History in plain language. One entry per event: who, what, when, and for an
// edit the old → new value of each field under a friendly label. Older edits were stored as one
// row per field; rows from the same person within a few seconds are one save and are shown as
// one entry. Internal stage names never reach the screen.

const STAGE_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  created: 'Created',
  updated: 'Updated',
  data_edit: 'Edited',
  corrected: 'Resubmitted after revisions',
  revised: 'Revised in TrueContext',
  revision_approved: 'Revision approved (payroll variance)',
  rejected: 'Sent back for revisions',
  approved: 'Approved',
  approval: 'Approved',
  're-approved': 'Re-approved',
  declined: 'Declined',
  decline_withdrawn: 'Decline withdrawn',
  approval_withdrawn: 'Approval withdrawn',
  revisions_withdrawn: 'Revision request withdrawn',
  comment: 'Comment',
  comment_added: 'Comment',
  manual_comment: 'Comment',
  // HE-F21. Covers both answers - the comment says which, and why if a send failed.
  outbound_email: 'Vendor PDF email',
  vendor_email_sent: 'Vendor PDF emailed',
  repriced: 'Repriced at a new rate',
  backdated_rate_left: 'Backdated rate left as is',
  effort_applied: 'Efforts applied',
  labour_ot_split: 'Labour overtime split',
  tasks_left_unused: 'Unused tasks cancelled',
  draft: 'Draft saved',
  cancellation: 'Cancelled',
};

/** "backdated_rate_left" → "Backdated rate left" – never "Form backdated_rate_left". */
function humanize(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

export function formHistoryStageLabel(stage: string | null | undefined): string {
  const key = (stage ?? '').trim();
  return STAGE_LABELS[key] ?? humanize(key);
}

const FIELD_LABELS: Record<string, string> = {
  ActualStartTime: 'Start Time',
  ActualEndTime: 'End Time',
  TaskDurationHours: 'Task Duration (Hours)',
  TaskId: 'Task',
  Task: 'Task',
  EmployeeName: 'Employee',
  ForemanArea: 'Foreman Area',
  ServiceArea: 'Service Area',
  SubcontractorNameCom: 'Vendor',
  PONumber: 'PO Number',
  TicketNumber: 'Ticket Number',
  HiredEquipmentTicket: 'Ticket Number',
  UnitsMeasurement: 'Units of Measure',
  FuelPriceAdjustment: 'Fuel Surcharge',
  CalculatedCost: 'Amount',
  AmountBilled: 'Amount Billed',
  ClassRate: 'Class Rate',
  ActivityNumber: 'Activity',
  LineComments: 'Line Comment',
};

/** "TaskDurationHours" → "Task Duration (Hours)"; unknown names are split into words. */
export function formHistoryFieldLabel(field: string): string {
  const name = field.split('.').pop() ?? field;
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}


// V3-019 – hired equipment lines: only what the user changed, with its line number, a friendly
// label and money formatted. Amounts and the other values the server works out from the user's
// entries are not changes of their own.
const HE_LINE_PATH = /^WorkCompleted\.Tasks 1\.(\d+)(?:\.|$)/;
const HE_DERIVED_FIELDS = new Set([
  'CalculatedCost',
  'AmountBilled',
  'HiredEquipmentRate',
  '8hourRate',
  'Overtime12HourRate',
  'FuelPriceAdjustment',
  'ResourceId',
  'ResourceType',
  'BundleComponents',
]);
const HE_LINE_LABELS: Record<string, string> = {
  ActualStartTime: 'Date',
  Task: 'Task',
  HoursLabour: 'Qty',
  RateBlueBook: 'Rate',
  UnitsMeasurement: 'Unit',
  SubcontractorName: 'Resource',
  SubcontractorNameCom: 'Vendor',
  PONumber: 'PO Number',
  TicketNumber: 'Ticket Number',
  PrimaryVehicleType: 'Vehicle Type',
  FuelSurchargeEligible: 'Fuel Surcharge',
  FuelSurchargeOverridePerHour: 'Fuel Surcharge Override',
  OvertimeEligible: 'Overtime',
  LumpSumDescription: 'Description',
  VendorMaterial: 'Vendor Material',
  'Comments 5': 'Vendor Material Notes',
  LineComments: 'comment',
};
const MONEY_FIELDS = new Set([
  'RateBlueBook',
  'HiredEquipmentRate',
  'CalculatedCost',
  'AmountBilled',
  'FuelPriceAdjustment',
  'FuelSurchargeOverridePerHour',
  'ClassRate',
]);

/** 1-based line of a hired equipment line field, from its path; null for anything else. */
export function heLineNumber(path: string | null | undefined): number | null {
  const match = (path ?? '').match(HE_LINE_PATH);
  return match ? Number(match[1]) + 1 : null;
}

/** Stored line comments (JSON from older saves, or text) as what was written. */
function commentText(value: string | null | undefined): string {
  if (value == null || value.trim() === '') return '';
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => (typeof item === 'string' ? item : typeof item?.text === 'string' ? item.text : ''))
        .map((text: string) => text.trim())
        .filter(Boolean)
        .join('; ');
    }
  } catch {
    // Already text.
  }
  return value.trim().split('\n').filter(Boolean).join('; ');
}

function displayValue(name: string, value: string | null | undefined): string {
  if (value == null || value === '') return EMPTY_MARK;
  if (MONEY_FIELDS.has(name)) {
    const n = Number(value);
    if (value.trim() !== '' && Number.isFinite(n)) return formatDollars(n);
  }
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return value;
}

/** One History line for a field change, or null when it is not shown (derived, or no change). */
export function formHistoryChangeLine(row: Pick<FormHistory, 'field' | 'path' | 'oldValue' | 'newValue'>): string | null {
  const name = (row.field ?? '').split('.').pop() ?? '';
  const line = heLineNumber(row.path);
  const isHeLine = line != null && (row.path ?? '').includes('EquipmentTimeWorkedJ');
  if (isHeLine && HE_DERIVED_FIELDS.has(name)) return null;
  if (name === 'LineComments') {
    const before = commentText(row.oldValue);
    const after = commentText(row.newValue);
    if (before === after) return null;
    const prefix = line != null ? `Line ${line} comment` : 'Line comment';
    if (!before) return `${prefix} added: "${after}"`;
    if (!after) return `${prefix} removed: "${before}"`;
    return `${prefix}: "${before}" → "${after}"`;
  }
  const label = line != null && HE_LINE_LABELS[name] ? `Line ${line} ${HE_LINE_LABELS[name]}` : formHistoryFieldLabel(row.field ?? '');
  const separator = line != null && HE_LINE_LABELS[name] ? ' ' : ': ';
  return `${label}${separator}${displayValue(name, row.oldValue)} → ${displayValue(name, row.newValue)}`;
}

export interface FormHistoryEntry {
  key: string;
  title: string;
  /** "Start Time: 07:00 → 08:00", one per field changed. */
  changes: string[];
  comments?: string;
  by: string;
  createdAt: string;
}

/** Rows written by one save land within a few seconds of each other. */
const SAME_SAVE_MS = 5_000;

function byLine(row: FormHistory): string {
  if (!row.user) return 'By System';
  const name = `${row.user.name ?? ''} ${row.user.lastName ?? ''}`.trim();
  return `By ${name || 'Unknown'}`;
}

/** Newest first; a save's per-field rows become one entry listing every field it changed. */
export function formHistoryEntries(history: FormHistory[]): FormHistoryEntry[] {
  const sorted = [...history].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const entries: FormHistoryEntry[] = [];
  let open: { entry: FormHistoryEntry; userId?: number; at: number } | null = null;

  for (const row of sorted) {
    const at = new Date(row.createdAt).getTime();
    if (row.field) {
      const change = formHistoryChangeLine(row);
      const sameSave = open && open.userId === row.userId && Math.abs(open.at - at) <= SAME_SAVE_MS;
      if (change === null) {
        // A derived amount or an unchanged comment: part of the save, never a line of its own.
        if (!sameSave) open = null;
        continue;
      }
      if (open && sameSave) {
        open.entry.changes.push(change);
        open.entry.title = `Edited ${open.entry.changes.length} fields`;
        if (row.comments && !open.entry.comments) open.entry.comments = row.comments;
        continue;
      }
      const entry: FormHistoryEntry = {
        key: String(row.id),
        title: 'Edited 1 field',
        changes: [change],
        comments: row.comments || undefined,
        by: byLine(row),
        createdAt: row.createdAt,
      };
      entries.push(entry);
      open = { entry, userId: row.userId, at };
      continue;
    }
    open = null;
    entries.push({
      key: String(row.id),
      title: formHistoryStageLabel(row.stage) || 'Updated',
      changes: [],
      comments: row.comments || undefined,
      by: byLine(row),
      createdAt: row.createdAt,
    });
  }
  return entries;
}
